import {
  BALANCE,
  formatCount,
  formatDuration,
  formatNumber,
  formatPercent,
  isStarving,
  offlineCapSeconds,
  timeToTarget,
} from '../engine';
import { gameStore } from '../game/store';
import type { Snapshot } from '../game/store';
import { Glyph } from './Glyph';

interface SidebarProps {
  snapshot: Snapshot;
}

export function Sidebar({ snapshot }: SidebarProps) {
  const { state, production, modifiers } = snapshot;
  const notation = state.settings.notation;

  const starving = isStarving(production);
  const shelterUse = Math.min(100, (state.population / production.shelter) * 100);
  const nutritionCover =
    production.nutritionDemand <= 0
      ? 1
      : production.nutritionSupplied / production.nutritionDemand;

  const vigorBonus = state.vigorStacks * modifiers.vigorPerStack;
  const vigorFill = (state.vigorStacks / Math.max(1, modifiers.vigorMaxStacks)) * 100;

  const growthSeconds =
    production.shelter - state.population > 0.5 && !starving
      ? timeToTarget(state.population, Math.floor(state.population) + 1, growthRate(snapshot))
      : Infinity;

  return (
    <aside>
      <section className="panel">
        <h2 className="panel-title">The Fire</h2>

        <div className="setting-row" style={{ paddingTop: 0 }}>
          <div>
            <div className="setting-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Glyph id="tribe" size={16} /> Tribe
            </div>
            <div className="setting-desc">
              {production.idlePopulation} idle · shelter {formatCount(production.shelter)}
            </div>
          </div>
          <div className="stat">
            <span className="stat-value accent">{formatCount(state.population)}</span>
          </div>
        </div>
        <div className="bar">
          <div
            className={`bar-fill${shelterUse >= 99.5 ? ' rust' : ''}`}
            style={{ width: `${shelterUse}%` }}
          />
        </div>

        <div className="setting-row">
          <div>
            <div className="setting-label">Food</div>
            <div className="setting-desc">
              {starving
                ? 'The tribe is starving.'
                : Number.isFinite(growthSeconds)
                  ? `Next birth in ${formatDuration(growthSeconds)}`
                  : shelterUse >= 99.5
                    ? 'No room for more.'
                    : 'Fed, but not growing.'}
            </div>
          </div>
          <div className="stat">
            <span className={`stat-value${starving ? '' : ' accent'}`}>
              {Math.round(Math.min(1, nutritionCover) * 100)}%
            </span>
          </div>
        </div>
        <div className="bar">
          <div
            className={`bar-fill${starving ? ' rust' : ' moss'}`}
            style={{ width: `${Math.min(100, nutritionCover * 100)}%` }}
          />
        </div>
      </section>

      <section className="panel">
        <h2 className="panel-title">
          Lend a Hand
          <span className="hint">Optional</span>
        </h2>

        <button
          type="button"
          className="btn primary"
          style={{ width: '100%' }}
          onClick={() => gameStore.gather()}
        >
          Work alongside them
        </button>

        <div className="setting-row" style={{ borderBottom: 'none' }}>
          <div>
            <div className="setting-label">Vigor</div>
            <div className="setting-desc">
              {state.vigorStacks > 0
                ? `${formatPercent(vigorBonus)} to everything · fades in ${Math.ceil(
                    state.vigorDecay,
                  )}s`
                : `Each hand lent gives ${BALANCE.manualGatherSeconds}s of output and a stack.`}
            </div>
          </div>
          <div className="stat">
            <span className="stat-value accent">
              {state.vigorStacks}/{Math.floor(modifiers.vigorMaxStacks)}
            </span>
          </div>
        </div>
        <div className="bar">
          <div className="bar-fill" style={{ width: `${vigorFill}%` }} />
        </div>
      </section>

      <section className="panel">
        <h2 className="panel-title">Away</h2>
        <p className="setting-desc">
          The tribe carries on without you for up to{' '}
          <strong>{formatDuration(offlineCapSeconds(state))}</strong>. Everything is simulated —
          nothing is estimated.
        </p>
        <div className="card-effects" style={{ marginTop: 8 }}>
          <span className="effect-chip">
            Knowledge {formatNumber(state.ancestralKnowledge, notation)}
          </span>
          <span className="effect-chip">Rites {state.rites}</span>
        </div>
      </section>
    </aside>
  );
}

/** Population gained per second at the current surplus, for the birth estimate. */
function growthRate(snapshot: Snapshot): number {
  const { state, production, modifiers } = snapshot;
  const room = production.shelter - state.population;
  if (room <= 0) return 0;
  return room * BALANCE.baseGrowthRate * modifiers.growth;
}
